import { Button, Flex, message, Modal } from "antd";
import { ExpenseType } from "@/types/expenseType";
import useModal from "@/hooks/useModal";
import { ExpenseDraft } from "@/pages/Expenses/RecordExpense/types/expenseDraft";
import ExpenseDraftEditModal from "./components/EditModal";
import {
  useCreateExpensesBulk,
  useGetExpenseDescriptions,
  useGetExpenseSuppliers,
} from "@/pages/Expenses/RecordExpense/hooks/useExpenses";
import { useMemo, useState } from "react";
import {
  useGetExpenseDrafts,
  useDeleteExpenseDraft,
  useDeleteExpenseDraftBulk,
} from "@/pages/Expenses/RecordExpense/hooks/useExpenseDrafts";
import { useAuth } from "@/context/AuthContext";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import { SortCondition } from "@/components/table/hooks/useSort";
import Table, { FilterOptions } from "@/components/table";
import { getColumns } from "./utils/table";

interface ExpenseDraftsProps {
  expenseTypes: ExpenseType[];
}

const ExpenseDrafts = ({ expenseTypes }: ExpenseDraftsProps) => {
  const [sort, setSort] = useState<SortCondition | undefined>({
    field: "created_at",
    order: "desc",
  });
  const [filters, setFilters] = useState<FilterCondition[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
    setModalData: setEditModalData,
  } = useModal<ExpenseDraft>();

  const { mutate: submitDrafts } = useCreateExpensesBulk();
  const { mutate: deleteDraft } = useDeleteExpenseDraft();
  const { mutate: deleteDraftBulk } = useDeleteExpenseDraftBulk();

  const { user } = useAuth();

  // Fetch drafts from API
  const { data: expenseDrafts = [], isLoading } = useGetExpenseDrafts({
    config: {
      params: {
        user_id_eq: user?.id,
        sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
        filters: filters.length > 0 ? filters : undefined,
        limit: -1,
      },
    },
  });

  // Get filter options
  const { data: options } = useGetExpenseDrafts({
    config: {
      params: {
        only: "expense_type.id,expense_type.name,description.id,description.name,supplier.id,supplier.name,unit.id,unit.name",
        user_id_eq: user?.id,
        limit: -1,
      },
    },
  });

  const filterOptions = useMemo(
    () => ({
      expense_type_id: (options || []).reduce((acc, item) => {
        if (
          item.expense_type &&
          !acc.some((option) => option.value === item?.expense_type?.id)
        ) {
          acc.push({
            text: item.expense_type.name,
            value: item.expense_type.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      description_id: (options || []).reduce((acc, item) => {
        if (
          item.description &&
          !acc.some((option) => option.value === item?.description?.id)
        ) {
          acc.push({ text: item.description.name, value: item.description.id });
        }
        return acc;
      }, [] as FilterOptions[]),
      supplier_id: (options || []).reduce((acc, item) => {
        if (
          item.supplier &&
          !acc.some((option) => option.value === item?.supplier?.id)
        ) {
          acc.push({ text: item.supplier.name, value: item.supplier.id });
        }
        return acc;
      }, [] as FilterOptions[]),
      unit_id: (options || []).reduce((acc, item) => {
        if (
          item.unit &&
          !acc.some((option) => option.value === item?.unit?.id)
        ) {
          acc.push({ text: item.unit.name, value: item.unit.id });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  const descriptions = useGetExpenseDescriptions({
    config: {
      params: {
        expense_type_id_eq: editModalData?.expense_type_id,
        limit: -1,
      },
    },
  });
  const suppliers = useGetExpenseSuppliers({
    config: { params: { limit: -1 } },
  });

  const handleEdit = (expense: ExpenseDraft) => {
    setEditModalData(expense);
    openEditModal(expense);
  };

  const handleRemove = (expense: ExpenseDraft) => {
    Modal.confirm({
      title: "Remove this expense from drafts?",
      content: "This action cannot be undone.",
      okText: "Remove Expense",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        deleteDraft(expense.id, {
          onSuccess: () => {
            message.success("Draft removed successfully");
          },
        });
      },
    });
  };

  const handleClearAll = () => {
    Modal.confirm({
      title: "Clear all expenses from drafts?",
      content: "This action cannot be undone.",
      okText: "Clear All",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        const draftIds = expenseDrafts?.map((draft) => draft.id);
        deleteDraftBulk(draftIds, {
          onSuccess: () => {
            message.success("All drafts cleared successfully");
          },
          onError: () => {
            message.error("Failed to clear all drafts");
          },
        });
      },
    });
  };

  const handleSubmitAll = () => {
    setIsSubmitting(true);

    // Get draft IDs to delete after successful submission
    const draftIds = expenseDrafts?.map((draft) => draft.id);

    submitDrafts(
      {
        delete_drafts: draftIds || [],
      },
      {
        onSuccess: () => {
          message.success("Successfully submitted all expenses");
          setIsSubmitting(false);
        },
        onError: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  return (
    <>
      <Flex vertical gap={16}>
        <Table
          columns={(props) =>
            getColumns({
              ...props,
              onEdit: handleEdit,
              onDelete: handleRemove,
              filterOptions,
            })
          }
          dataSource={expenseDrafts || []}
          size="small"
          loading={isLoading}
          locale={{
            emptyText: null,
          }}
          scroll={{ x: "max-content" }}
          initialSort={sort}
          onFilter={setFilters}
          onSort={setSort}
          isHidden={expenseDrafts?.length === 0 && filters.length === 0}
        />

        <Flex
          justify="flex-end"
          align="center"
          gap={16}
          hidden={expenseDrafts?.length === 0}
        >
          <Button
            onClick={handleClearAll}
            danger
            disabled={expenseDrafts?.length === 0}
          >
            Clear All
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSubmitAll}
            loading={isSubmitting}
            disabled={expenseDrafts?.length === 0}
          >
            Submit All
          </Button>
        </Flex>
      </Flex>

      <ExpenseDraftEditModal
        data={editModalData}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        expenseTypes={expenseTypes}
        descriptions={descriptions || []}
        suppliers={suppliers || []}
      />
    </>
  );
};

export default ExpenseDrafts;
